import React, { useEffect, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, LabelList,
} from 'recharts';

import PanelCard from '../../performance-operations/components/PanelCard';
import {
  Clickable, ProgressFill, ProgressTrack, Sub, Table,
} from '../../performance-operations/styles';
import { usePanel } from '../../performance-operations/config';
import { bandRows, fetchBands, formatNumber } from '../config';

// E04 orta sol: gerçekleşme bantları (hedef düzeyi ham r; sonuç değeri → küçük grup gizlemesi: n < k bant ve tamamlayıcı
// bant gizlenir, gizlenen değer grafikte çizilmez) + hedef türüne göre kurumsal / birim / bireysel kırılım. Bant tıklaması
// (grafik ya da tablo — klavye) hedef listesine bant koşulu ekler: bant → hedef listesi → hedef detayı.
const LevelRow = styled.div`
  display: grid;
  grid-template-columns: 120px minmax(0, 1fr) 70px;
  gap: 10px;
  align-items: center;
  margin-bottom: 8px;
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const LevelTitle = styled.div`
  margin: 14px 0 8px;
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

export default function BandsPanel({
  filters, goalFilters, onMeta, onDrill, refreshKey,
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const theme = useTheme();
  const [table, setTable] = useState(false);
  const key = JSON.stringify([filters, goalFilters]);
  const panel = usePanel(() => fetchBands(filters, goalFilters), [key, refreshKey], !!filters.periodId);
  useEffect(() => {
    if (panel.data) onMeta?.('goalBands', { calculatedAt: panel.data.calculatedAt, stale: false });
  }, [panel.data, onMeta]);

  const rows = bandRows(panel.data?.bands, t);
  const empty = !panel.data || (panel.data.goalCount === 0 && (panel.data.levels || []).every((l) => !l.count));
  const select = (bandKey) => onDrill?.((f) => ({ ...f, band: bandKey }));
  const chartRows = rows.map((r) => ({ ...r, value: r.suppressed ? null : r.goals }));

  return (
    <PanelCard
      panelKey="goalBands"
      title={t('performance.goalAnalytics.panel.bands')}
      span={7}
      loading={panel.loading}
      error={panel.error}
      onRetry={panel.retry}
      empty={empty}
      emptyText={t('performance.goalAnalytics.panel.noGoals')}
      tableToggle
      showTable={table}
      onToggleTable={() => setTable((v) => !v)}
    >
      {table ? (
        <Table>
          <thead>
            <tr>
              <th>{t('performance.goalAnalytics.col.band')}</th>
              <th>{t('performance.goalAnalytics.col.goals')}</th>
              <th>{t('performance.goalAnalytics.col.persons')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key} data-row={r.key}>
                <td>
                  <Clickable onClick={() => select(r.key)} aria-label={r.label}>{r.label}</Clickable>
                </td>
                <td>{r.suppressed ? t('performance.goalAnalytics.kpi.suppressed') : formatNumber(r.goals, lang, 0)}</td>
                <td>{r.suppressed ? '-' : formatNumber(r.persons, lang, 0)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : (
        <div style={{ width: '100%', height: 220 }}>
          <ResponsiveContainer>
            <BarChart data={chartRows} margin={{ top: 16, right: 8, left: 0, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: theme.metrics.font.size.xs }} interval={0} />
              <YAxis allowDecimals={false} tick={{ fontSize: theme.metrics.font.size.xs }} />
              <Tooltip />
              <Bar
                dataKey="value"
                name={t('performance.goalAnalytics.col.goals')}
                fill={theme.colors.primary.main}
                cursor="pointer"
                onClick={(d) => select((d.payload || d).key)}
              >
                <LabelList dataKey="value" position="top" style={{ fontSize: theme.metrics.font.size.xs }} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
      {rows.some((r) => r.suppressed) ? <Sub>{t('performance.goalAnalytics.panel.suppressedNote', { k: panel.data?.threshold })}</Sub> : null}

      <LevelTitle>{t('performance.goalAnalytics.panel.levels')}</LevelTitle>
      {(panel.data?.levels || []).map((l) => (
        <LevelRow key={l.level} data-level={l.level}>
          <div>
            <div>{t(`performance.goalAnalytics.level.${l.level}`)}</div>
            <Sub>{t('performance.goalAnalytics.panel.levelCount', { count: l.count })}</Sub>
          </div>
          <ProgressTrack><ProgressFill $pct={l.avgProgressPct ?? 0} /></ProgressTrack>
          <div>
            {l.suppressionReason
              ? t('performance.goalAnalytics.kpi.suppressed')
              : (l.avgProgressPct != null ? `${formatNumber(l.avgProgressPct, lang)}%` : t('performance.goalAnalytics.kpi.noData'))}
          </div>
        </LevelRow>
      ))}
    </PanelCard>
  );
}
